// ============================================
// FILE: src/components/dashboard/ProfileOverview.jsx
// PURPOSE: User profile summary card with edit modal
// ============================================

import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { User, Mail, Calendar, Shield, Phone } from 'lucide-react';
import EditProfileModal from './EditProfileModal';

export default function ProfileOverview({ user, onUpdate }) {
  console.log('🎨 [ProfileOverview] Rendering with user:', user);

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const handleEditClick = () => {
    console.log('[ProfileOverview] ✏️ Edit button clicked');
    setIsEditModalOpen(true);
  };

  const handleModalClose = () => {
    console.log('[ProfileOverview] 🚪 Modal closed');
    setIsEditModalOpen(false);
  };

  const handleProfileUpdate = (updatedUser) => {
    console.log('[ProfileOverview] ✅ Profile updated:', updatedUser);
    setIsEditModalOpen(false);
    onUpdate?.();
  };

  return (
    <>
      <Card>
        <CardHeader className="p-3 sm:p-4 md:p-6">
          <CardTitle className="flex items-center gap-2 text-base sm:text-lg md:text-xl">
            <User className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
            Profile Overview
          </CardTitle>
        </CardHeader>
        <CardContent className="p-3 sm:p-4 md:p-6 pt-0">
          {/* Profile Header - responsive layout and sizing */}
          <div className="flex items-center gap-3 sm:gap-4 mb-4 sm:mb-6">
            <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-primary/10 flex items-center justify-center text-xl sm:text-2xl font-bold text-primary flex-shrink-0">
              {user.fullName?.charAt(0).toUpperCase() || 'U'}
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="text-base sm:text-lg md:text-xl font-semibold truncate">{user.fullName || 'User'}</h3>
              <p className="text-xs sm:text-sm text-muted-foreground truncate">{user.email}</p>
              {user.emailVerified && (
                <span className="inline-flex items-center gap-1 text-[10px] sm:text-xs text-green-600 mt-0.5 sm:mt-1">
                  <Shield className="w-3 h-3 flex-shrink-0" /> Verified
                </span>
              )}
            </div>
          </div>

          {/* Contact Info - responsive spacing and text */}
          <div className="space-y-2 sm:space-y-3 text-xs sm:text-sm">
            <div className="flex items-center gap-2">
              <Mail className="w-3 h-3 sm:w-4 sm:h-4 text-muted-foreground flex-shrink-0" />
              <span className="truncate">{user.email}</span>
            </div>

            {user.phone && (
              <div className="flex items-center gap-2">
                <Phone className="w-3 h-3 sm:w-4 sm:h-4 text-muted-foreground flex-shrink-0" />
                <span className="truncate">{user.phone}</span>
              </div>
            )}
         
            <div className="flex items-center gap-2">
              <Calendar className="w-3 h-3 sm:w-4 sm:h-4 text-muted-foreground flex-shrink-0" />
              <span className="truncate">Member since {new Date(user.memberSince).toLocaleDateString()}</span>
            </div>
          </div>

          {/* Edit Button - responsive sizing */}
          <button 
            onClick={handleEditClick}
            className="w-full mt-4 sm:mt-6 py-2 text-xs sm:text-sm border rounded-md hover:bg-muted transition h-9 sm:h-10"
          >
            Edit Profile
          </button>
        </CardContent>
      </Card>

      <EditProfileModal
        isOpen={isEditModalOpen}
        onClose={handleModalClose}
        user={user}
        onUpdate={handleProfileUpdate}
      />
    </>
  );
}